import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { dateFormate } from '../../../utils/helper.jsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { parseTemplateListResponse } from '../adapters/templateListAdapter';
import { templateService } from '../services/templateService';
import { templateReportingService } from '../services/templateReportingService';
import { templateFormAtom } from '../state/templateAtoms';
import { normalizeTemplate } from '../utils/normalizeTemplate';
import { USE_WHATSAPP_REPORTING } from '../constants/reportingConfig';
import {
  buildReportingGraphFromDaily,
  mapCtaRowsToButtonEngagement,
  mapDailyRowToSummaryRow,
} from '../utils/reportingMappers';
import useTemplateReportingScope from './useTemplateReportingScope';

function formatDateForLegacyApi(date) {
  if (!date) return '';
  if (typeof date === 'string') return date;
  return dateFormate(date, 'DD/MM/YYYY');
}

function buildLegacyGraphData(summaryData) {
  const list = summaryData?.data ?? [];
  if (!list.length) return null;
  return {
    series: [
      { data: list.map((item) => item.sent_count || 0), label: 'Sent' },
      { data: list.map((item) => item.delivered_count || 0), label: 'Delivered' },
      { data: list.map((item) => item.read_count || 0), label: 'Read' },
    ],
    xAxis: [{ scaleType: 'point', data: list.map((item) => item.sent_date || 'N/A') }],
  };
}

function buildLegacyButtonEngagement(summaryData) {
  const buttonList = summaryData?.button_list ?? [];
  const counts = summaryData?.message_counts ?? {};
  const totalButtonCount =
    (counts.button_count_1 || 0) + (counts.button_count_2 || 0) + (counts.button_count_3 || 0);
  if (!buttonList.length) return [];
  return buttonList.slice(0, 3).map((button, index) => {
    const clicks = counts[`button_count_${index + 1}`] || 0;
    const clickRate =
      totalButtonCount > 0 ? `${((clicks / totalButtonCount) * 100).toFixed(2)}%` : '0%';
    return {
      label: button?.name || `Button ${index + 1}`,
      type: 'Quick reply',
      totalClicks: clicks,
      clickRate,
    };
  });
}

function buildTemplateMetaMap(templates = []) {
  const map = {};
  templates.forEach((item) => {
    const id = String(item?.md_id ?? item?.template_id ?? '');
    if (!id) return;
    map[id] = {
      name: item?.name ?? '',
      bot_number: item?.bot_number ?? '',
      category: item?.category ?? '',
      language: item?.language ?? '',
    };
  });
  return map;
}

function mapOrgTemplateSendRows(reportRows, metaMap) {
  return (reportRows ?? []).map((row) => {
    const meta = metaMap[String(row.template_id)] ?? {};
    return {
      ...row,
      display_name: row.template_name || meta.name || row.template_id,
      bot_number: row.bot_number || meta.bot_number || '—',
      category: meta.category ?? '',
      language: meta.language ?? '',
    };
  });
}

export default function useTemplatePerformance() {
  const { templateName, botNumber } = useParams();
  const isOrgMode = !templateName || !botNumber;
  const { showSnackBar } = useSnackBar();
  const [template, setTemplate] = useRecoilState(templateFormAtom);
  const {
    companyId,
    filters,
    apiScope,
    applyDateRange,
    setTemplateId,
    updateFilters,
  } = useTemplateReportingScope();

  const [overview, setOverview] = useState({});
  const [dailyRows, setDailyRows] = useState([]);
  const [orgSendsRows, setOrgSendsRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [ctaRows, setCtaRows] = useState([]);
  const [failureRows, setFailureRows] = useState([]);
  const [buttonEngagement, setButtonEngagement] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [legacySummary, setLegacySummary] = useState({
    message_counts: {},
    data: [],
    button_list: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [eventsPending, setEventsPending] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const orgScope = useMemo(
    () => ({
      company_id: companyId,
      start_date: filters.start_date,
      end_date: filters.end_date,
    }),
    [companyId, filters.end_date, filters.start_date]
  );

  const fetchOrgReportingData = useCallback(async () => {
    if (!companyId) return;

    const [overviewPayload, cta, failures, reportRows, listRes, activityRes] = await Promise.all([
      templateReportingService.getOverview(orgScope),
      templateReportingService.getCta(orgScope),
      templateReportingService.getFailures(orgScope),
      templateReportingService.getTemplates(orgScope),
      templateService.getTemplates({
        organisation: companyId,
        botNumber: '',
        page: 0,
        limit: 500,
        archived: 'false',
      }),
      templateService.getOrgTemplateActivity(companyId, 100),
    ]);

    const parsed = parseTemplateListResponse(listRes);
    const metaMap = buildTemplateMetaMap(parsed.templates ?? []);

    setOverview(overviewPayload ?? {});
    setCtaRows(cta ?? []);
    setFailureRows(failures ?? []);
    setEventsPending(overviewPayload?.events_pending ?? 0);
    setOrgSendsRows(mapOrgTemplateSendRows(reportRows, metaMap));
    setGraphData(null);
    setButtonEngagement(mapCtaRowsToButtonEngagement(cta, overviewPayload, null));
    setDailyRows([]);

    if (activityRes?.status === 200) {
      setActivities(Array.isArray(activityRes?.data?.data) ? activityRes.data.data : []);
    } else {
      setActivities([]);
    }
  }, [companyId, orgScope]);

  const fetchReportingData = useCallback(async () => {
    if (!filters.template_id || !companyId) return;

    const scope = {
      company_id: companyId,
      bot_number: botNumber,
      template_id: filters.template_id,
      start_date: filters.start_date,
      end_date: filters.end_date,
    };

    const [detail, dailyPayload, cta, failures, overviewPayload, activityRes] = await Promise.all([
      templateReportingService.getTemplateDetail(filters.template_id, scope),
      templateReportingService.getTemplateDaily(filters.template_id, scope, {
        page: 0,
        limit: 100,
      }),
      templateReportingService.getCta(scope),
      templateReportingService.getFailures(scope),
      templateReportingService.getOverview(scope),
      templateService.getTemplateActivity(templateName, botNumber, 50),
    ]);

    const rows = dailyPayload?.results ?? [];
    setOverview(detail ?? {});
    setDailyRows(rows);
    setCtaRows(cta ?? []);
    setFailureRows(failures ?? []);
    setEventsPending(overviewPayload?.events_pending ?? 0);
    setGraphData(buildReportingGraphFromDaily(rows));
    setButtonEngagement(mapCtaRowsToButtonEngagement(cta, detail, template));
    setOrgSendsRows([]);

    if (activityRes?.status === 200) {
      setActivities(Array.isArray(activityRes?.data?.data) ? activityRes.data.data : []);
    } else {
      setActivities([]);
    }
  }, [
    botNumber,
    companyId,
    filters.end_date,
    filters.start_date,
    filters.template_id,
    template,
    templateName,
  ]);

  const fetchLegacyData = useCallback(async () => {
    if (!filters.template_id) return;

    const res = await templateService.getDateWiseSummary({
      ...filters,
      start_date: formatDateForLegacyApi(filters.start_date),
      end_date: formatDateForLegacyApi(filters.end_date),
    });

    if (res?.status === 200) {
      const data = res.data ?? { message_counts: {}, data: [], button_list: [] };
      setLegacySummary(data);
      setOverview(data.message_counts ?? {});
      setDailyRows(data.data ?? []);
      setGraphData(buildLegacyGraphData(data));
      setButtonEngagement(buildLegacyButtonEngagement(data));
      setCtaRows([]);
      setFailureRows([]);
      setOrgSendsRows([]);
      setActivities([]);
    }
  }, [filters]);

  const refreshPerformance = useCallback(async () => {
    if (!companyId) return;
    if (!isOrgMode && !filters.template_id) return;

    setIsLoading(true);
    try {
      if (USE_WHATSAPP_REPORTING) {
        if (isOrgMode) {
          await fetchOrgReportingData();
        } else {
          await fetchReportingData();
        }
      } else if (!isOrgMode) {
        await fetchLegacyData();
      }
    } catch (error) {
      showSnackBar(error?.message || 'Failed to load performance data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    companyId,
    fetchLegacyData,
    fetchOrgReportingData,
    fetchReportingData,
    filters.template_id,
    isOrgMode,
    showSnackBar,
  ]);

  const loadTemplate = useCallback(async () => {
    if (isOrgMode) {
      setTemplateId('');
      return;
    }
    if (!templateName || !botNumber) return;

    try {
      if (template?.name === templateName && template?.bot_number === botNumber && template?.md_id) {
        setTemplateId(template.md_id);
        return;
      }

      const res = await templateService.getTemplate(templateName, botNumber);
      if (res?.status === 200 && res?.data) {
        const normalized = normalizeTemplate(res.data);
        setTemplate(normalized);
        setTemplateId(normalized.md_id ?? '');
      }
    } catch (error) {
      showSnackBar(error?.message || 'Failed to load template', 'error');
    }
  }, [
    botNumber,
    isOrgMode,
    setTemplate,
    setTemplateId,
    showSnackBar,
    template?.bot_number,
    template?.md_id,
    template?.name,
    templateName,
  ]);

  useEffect(() => {
    if (!USE_WHATSAPP_REPORTING || isOrgMode) return;
    setButtonEngagement(mapCtaRowsToButtonEngagement(ctaRows, overview, template));
  }, [ctaRows, overview, template, isOrgMode]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  useEffect(() => {
    if (isOrgMode) {
      if (companyId) refreshPerformance();
      return;
    }
    if (filters.template_id) {
      refreshPerformance();
    }
  }, [
    companyId,
    filters.end_date,
    filters.start_date,
    filters.template_id,
    isOrgMode,
    refreshPerformance,
  ]);

  const handleSortingChange = useCallback(
    (sorting) => {
      if (!sorting?.length) return;
      const { id, desc } = sorting[0];
      updateFilters({
        sort_by: id || 'sent_date',
        order: desc ? 'desc' : 'asc',
      });
    },
    [updateFilters]
  );

  const syncReporting = useCallback(async () => {
    if (!USE_WHATSAPP_REPORTING || !companyId) return;
    setIsSyncing(true);
    try {
      const scope = isOrgMode
        ? orgScope
        : {
            company_id: companyId,
            bot_number: botNumber,
            template_id: filters.template_id,
            start_date: filters.start_date,
            end_date: filters.end_date,
          };
      const result = await templateReportingService.syncReporting(scope);
      const processed = result?.events_processed ?? 0;
      setEventsPending(result?.events_pending ?? 0);
      await refreshPerformance();
      showSnackBar(
        processed > 0 ? `Synced ${processed} event(s)` : 'Reporting data is up to date',
        'success'
      );
    } catch (error) {
      showSnackBar(error?.message || 'Failed to sync reporting data', 'error');
    } finally {
      setIsSyncing(false);
    }
  }, [
    botNumber,
    companyId,
    filters.end_date,
    filters.start_date,
    filters.template_id,
    isOrgMode,
    orgScope,
    refreshPerformance,
    showSnackBar,
  ]);

  const summaryRows = useMemo(() => {
    if (isOrgMode) return orgSendsRows;
    if (USE_WHATSAPP_REPORTING) {
      return dailyRows.map((row) => mapDailyRowToSummaryRow(row, filters.template_id));
    }
    return dailyRows;
  }, [dailyRows, filters.template_id, isOrgMode, orgSendsRows]);

  const paginatedSummaryRows = useMemo(() => {
    const page = filters.page ?? 0;
    const limit = filters.limit ?? 10;
    const start = page * limit;
    return summaryRows.slice(start, start + limit);
  }, [filters.limit, filters.page, summaryRows]);

  const displayOverview = useMemo(() => {
    if (USE_WHATSAPP_REPORTING) return overview;
    return legacySummary.message_counts ?? {};
  }, [legacySummary.message_counts, overview]);

  const hasChartData = graphData?.series?.some((series) =>
    series.data.some((value) => value > 0)
  );

  return {
    isOrgMode,
    templateName,
    botNumber,
    template,
    filters,
    apiScope,
    overview: displayOverview,
    dailyRows,
    summaryRows,
    paginatedSummaryRows,
    totalSummaryCount: summaryRows.length,
    orgSendsRows,
    activities,
    graphData,
    ctaRows,
    failureRows,
    buttonEngagement,
    isLoading,
    hasChartData,
    eventsPending,
    isSyncing,
    applyDateRange,
    updateFilters,
    handleSortingChange,
    syncReporting,
    refreshPerformance,
    usingReportingPipeline: USE_WHATSAPP_REPORTING,
  };
}
